/**
 * "Edit origin" on the Object Editor's 3D model (Blender's "affect only
 * origins"): the gizmo's move handles shift the model's origin — the point
 * it is placed by and rotates / scales around — while the model itself stays
 * put (`spec.model3d` position + pivot, see moveModelOrigin). A drag is one
 * editor transaction = one undo step.
 */

import * as THREE from 'three';
import type { ObjectModel3D } from 'shared-types';
import { moveModelOrigin, placedModelBounds } from '../../../potree/sketchObjects/custom/modelPlacement';
import type { GizmoFrame, GizmoSpace, TransformGizmoBinding } from '../../../potree/personas/tools/modules/transformGizmo/TransformGizmoBinding';
import type { Snapshot } from '../../../potree/personas/tools/ToolUndoRedoManager';
import type { EditorStore } from '../document/EditorStore';

/** Only moving: an origin has no size and its turn is the model's. */
const MOVE_HANDLES: ReadonlyArray<string> = ['translation.x', 'translation.y', 'translation.z', 'translation.free'];

/** Snap reach: 4% of the model's largest dimension, at least 1 cm. */
const SNAP_FRACTION = 0.04;
const SNAP_MIN = 0.01;

function snapTo(value: number, targets: readonly number[], reach: number): number {
  let best = value;
  let bestDistance = reach;
  for (const target of targets) {
    const distance = Math.abs(value - target);
    if (distance <= bestDistance) {
      best = target;
      bestDistance = distance;
    }
  }
  return best;
}

export class ModelOriginBinding implements TransformGizmoBinding {
  private base: ObjectModel3D | null = null;
  /** The model file's own bounds (no placement): what the origin snaps to. */
  private meshBounds: THREE.Box3 | null = null;

  constructor(private readonly store: EditorStore) {}

  setMeshBounds(bounds: THREE.Box3 | null): void {
    this.meshBounds = bounds;
  }

  private get model(): ObjectModel3D | null {
    return this.store.getState().spec.model3d ?? null;
  }

  /** The origin at world axes: moving it along the model's tilted axes would only confuse. */
  getFrame(): GizmoFrame | null {
    const model = this.model;
    return model ? { position: new THREE.Vector3(...model.position), quaternion: new THREE.Quaternion() } : null;
  }

  getSpace(): GizmoSpace {
    return 'world';
  }

  getEnabledHandles(): ReadonlyArray<string> | null {
    return MOVE_HANDLES;
  }

  beginDrag(): void {
    this.base = this.model;
    this.store.begin();
  }

  /**
   * Snaps onto the model's box when it comes close — its sides, center and
   * bottom / top, per axis (so it can slide along an edge). Shift or
   * Ctrl/Cmd = free move.
   */
  applyTranslation(worldDelta: THREE.Vector3, override: boolean): void {
    const base = this.base;
    if (!base) return;
    const target = new THREE.Vector3(...base.position).add(worldDelta);
    if (!override && this.meshBounds) {
      const box = placedModelBounds(base, this.meshBounds);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const reach = Math.max(SNAP_MIN, SNAP_FRACTION * Math.max(size.x, size.y, size.z));
      target.x = snapTo(target.x, [box.min.x, center.x, box.max.x], reach);
      target.y = snapTo(target.y, [box.min.y, center.y, box.max.y], reach);
      target.z = snapTo(target.z, [box.min.z, center.z, box.max.z], reach);
    }
    const next = moveModelOrigin(base, target);
    this.store.apply((spec) => ({ ...spec, model3d: next }));
  }

  applyRotation(): void {
    // Not offered (see MOVE_HANDLES).
  }

  applyScale(): void {
    // Not offered (see MOVE_HANDLES).
  }

  /** The editor store keeps the undo history: nothing for a tool undo stack. */
  commitDrag(): Snapshot | null {
    this.base = null;
    this.store.commit();
    return null;
  }

  cancelDrag(): void {
    this.base = null;
    this.store.rollback();
  }
}
